// Role-based permissions configuration
export const roles = {
  superAdmin: "superAdmin",
  admin: "admin",
  moderator: "moderator",
  accountant: "accountant",
};

// Permission keys
export const permissions = {
  // Dashboard
  VIEW_DASHBOARD: "view_dashboard",

  // Users Management
  VIEW_STUDENTS: "view_students",
  VIEW_TUTORS: "view_tutors",
  EDIT_STUDENTS: "edit_students",
  EDIT_TUTORS: "edit_tutors",
  DELETE_STUDENTS: "delete_students",
  DELETE_TUTORS: "delete_tutors",

  // Lessons
  VIEW_LESSONS: "view_lessons",
  EDIT_LESSONS: "edit_lessons",
  DELETE_LESSONS: "delete_lessons",

  // Subjects
  VIEW_SUBJECTS: "view_subjects",
  EDIT_SUBJECTS: "edit_subjects",
  DELETE_SUBJECTS: "delete_subjects",

  // Plans
  VIEW_PLANS: "view_plans",
  EDIT_PLANS: "edit_plans",

  // Financial
  VIEW_FINANCIAL: "view_financial",
  EDIT_FINANCIAL: "edit_financial",
  DELETE_FINANCIAL: "delete_financial",

  // Settings
  VIEW_SETTINGS: "view_settings",
  EDIT_SETTINGS: "edit_settings",

  // Inbox
  VIEW_INBOX: "view_inbox",
  REPLY_INBOX: "reply_inbox",

  // Employees
  VIEW_EMPLOYEES: "view_employees",
  EDIT_EMPLOYEES: "edit_employees",

  // Activity Log
  VIEW_ACTIVITY_LOG: "view_activity_log",

  // Verification (spec 026)
  REVIEW_VERIFICATION: "review_verification",

  // Wallet adjustment (phase-2 Slice W)
  ADJUST_WALLET: "adjust_wallet",

  // Leads (phase-2 Slice L) — staff-wide, mirrors the rules' isStaff() (spec 026 D7)
  VIEW_LEADS: "view_leads",

  // Spec 044 D5 — granted per person (users.grants), not per role. See GRANTS.
  VIEW_REPORTS: "view_reports",
  SEND_NOTIFICATIONS: "send_notifications",

  // Ban/unban an account (phase-2 task P2) — admin+ (spec 044 D4 keeps it there).
  BAN_ACCOUNTS: "ban_accounts",
  // Remove a student's rating of a tutor (spec 026 D24) — moderator+ since spec 044 D3,
  // the same rank the moderateRating callable checks server-side.
  MODERATE_RATINGS: "moderate_ratings",

  // Settings cards (phase-2 task P3). MANAGE_STAFF is admin+ since spec 044 D4 — the
  // server (setStaffRole) keeps an admin inside moderator/accountant/user; EDIT_VERSION
  // stays superAdmin-only because the version switch can lock every app user out.
  MANAGE_STAFF: "manage_staff",
  EDIT_VERSION: "edit_version",
};

// Role to permissions mapping
export const rolePermissions = {
  [roles.superAdmin]: [
    permissions.VIEW_DASHBOARD,
    permissions.VIEW_STUDENTS,
    permissions.VIEW_TUTORS,
    permissions.EDIT_STUDENTS,
    permissions.EDIT_TUTORS,
    permissions.DELETE_STUDENTS,
    permissions.DELETE_TUTORS,
    permissions.VIEW_LESSONS,
    permissions.EDIT_LESSONS,
    permissions.DELETE_LESSONS,
    permissions.VIEW_SUBJECTS,
    permissions.EDIT_SUBJECTS,
    permissions.DELETE_SUBJECTS,
    permissions.VIEW_PLANS,
    permissions.EDIT_PLANS,
    permissions.VIEW_FINANCIAL,
    permissions.EDIT_FINANCIAL,
    permissions.DELETE_FINANCIAL,
    permissions.VIEW_SETTINGS,
    permissions.EDIT_SETTINGS,
    permissions.VIEW_INBOX,
    permissions.REPLY_INBOX,
    permissions.VIEW_EMPLOYEES,
    permissions.EDIT_EMPLOYEES,
    permissions.VIEW_ACTIVITY_LOG,
    permissions.REVIEW_VERIFICATION,
    permissions.ADJUST_WALLET,
    permissions.VIEW_LEADS,
    permissions.BAN_ACCOUNTS,
    permissions.MODERATE_RATINGS,
    permissions.VIEW_REPORTS,
    permissions.SEND_NOTIFICATIONS,
    permissions.MANAGE_STAFF,
    permissions.EDIT_VERSION,
  ],

  [roles.admin]: [
    permissions.VIEW_DASHBOARD,
    permissions.VIEW_STUDENTS,
    permissions.VIEW_TUTORS,
    permissions.EDIT_STUDENTS,
    permissions.EDIT_TUTORS,
    permissions.VIEW_LESSONS,
    permissions.EDIT_LESSONS,
    permissions.VIEW_SUBJECTS,
    permissions.EDIT_SUBJECTS,
    permissions.VIEW_PLANS,
    permissions.EDIT_PLANS,
    permissions.VIEW_FINANCIAL,
    permissions.EDIT_FINANCIAL,
    permissions.VIEW_SETTINGS,
    permissions.VIEW_INBOX,
    permissions.REPLY_INBOX,
    permissions.VIEW_EMPLOYEES,
    permissions.VIEW_ACTIVITY_LOG,
    permissions.REVIEW_VERIFICATION,
    permissions.ADJUST_WALLET,
    permissions.VIEW_LEADS,
    permissions.BAN_ACCOUNTS,
    permissions.MODERATE_RATINGS,
    permissions.VIEW_REPORTS,
    permissions.SEND_NOTIFICATIONS,
    // Spec 044 D4: an admin adds and removes moderators. The server (setStaffRole)
    // keeps an admin inside moderator/accountant/user; this only opens the page.
    permissions.MANAGE_STAFF,
  ],

  // Spec 044 D3: moderators are generalists — support, trust & safety, daily
  // operations, growth. They act, not just look. What they still cannot do is D4's
  // list: ban, manage staff, edit the catalogue, record a payout. EDIT_LESSONS is
  // D9's staff doors (slice 3) — cancel as staff, mark finished, open a ticket —
  // each a callable; the direct bookings edit it used to open is gone.
  [roles.moderator]: [
    permissions.VIEW_DASHBOARD,
    permissions.VIEW_STUDENTS,
    permissions.VIEW_TUTORS,
    permissions.VIEW_LESSONS,
    permissions.EDIT_LESSONS,
    permissions.VIEW_SUBJECTS,
    permissions.VIEW_PLANS,
    permissions.VIEW_FINANCIAL,
    permissions.VIEW_INBOX,
    permissions.REPLY_INBOX,
    permissions.VIEW_EMPLOYEES,
    permissions.VIEW_SETTINGS,
    permissions.VIEW_LEADS,
    permissions.REVIEW_VERIFICATION,
    permissions.MODERATE_RATINGS,
    permissions.ADJUST_WALLET,
  ],

  [roles.accountant]: [
    permissions.VIEW_DASHBOARD,
    permissions.VIEW_STUDENTS,
    permissions.VIEW_TUTORS,
    permissions.VIEW_LESSONS,
    permissions.VIEW_FINANCIAL,
    // 053 D2: the accountant makes the transfers, so records them (the Payouts button, the Queue's Payouts due).
    permissions.EDIT_FINANCIAL,
    permissions.VIEW_PLANS,
    permissions.VIEW_LEADS,
  ],
};

// Spec 044 D5: two features are granted per person, not per role. The keys are what
// users/{uid}.grants holds (server-only; setStaffRole is the only writer); each maps
// to the permission its page checks. Admin and superAdmin carry both through their
// role lists, so a grant only ever matters for a moderator or accountant.
export const GRANTS = {
  reports: permissions.VIEW_REPORTS,
  notifications: permissions.SEND_NOTIFICATIONS,
};

// Everything one staff member may do: the role's list plus their own grants. Takes
// the merged user AuthContext provides (`{ role, grants }`) — or, for the call
// sites that predate 044, a bare role string, which has no grants. Anything in
// `grants` that GRANTS does not name is ignored: the vocabulary lives here.
export const permissionsFor = (userOrRole) => {
  const isString = typeof userOrRole === "string";
  const role = isString ? userOrRole : userOrRole?.role;
  const grants = isString ? [] : userOrRole?.grants;
  const fromRole = rolePermissions[role] || [];
  const fromGrants = (Array.isArray(grants) ? grants : [])
    .map((g) => (Object.prototype.hasOwnProperty.call(GRANTS, g) ? GRANTS[g] : undefined))
    .filter(Boolean);
  return [...new Set([...fromRole, ...fromGrants])];
};

// Helper function to check if user has a specific permission
export const hasPermission = (userOrRole, permission) =>
  permissionsFor(userOrRole).includes(permission);

// Helper function to check if user has any of the specified permissions
export const hasAnyPermission = (userRole, permissionList) => {
  return permissionList.some((permission) =>
    hasPermission(userRole, permission)
  );
};

// Helper function to check if user has all of the specified permissions
export const hasAllPermissions = (userRole, permissionList) => {
  return permissionList.every((permission) =>
    hasPermission(userRole, permission)
  );
};

// The four roles the rules' isStaff() also names (spec 024 D8). Case-sensitive
// on purpose: role strings are written by us, not typed by users.
export const STAFF_ROLES = Object.values(roles);
export const isStaffRole = (role) => STAFF_ROLES.includes(role);

// Admin and up — the rules' isAdmin(): the rank that may delete any staff note (spec 059).
export const isAdminRole = (role) => role === roles.admin || role === roles.superAdmin;
